import type {
	ChatMessage,
	ChatRequestBody,
	ChatStreamChunk,
	ChatStreamEvent,
	ChatToolSelection,
	ToolApprovalLevel,
} from '#layers/ai/types/chat';
import type { AiContextItem } from '#layers/ai/types/context';
import { joinURL } from 'ufo';
import { useProjectApiBaseUrl } from '#layers/base';

const chatStreamEventTypes = [
	'messageStart',
	'contentBlockStart',
	'contentBlockDelta',
	'contentBlockStop',
	'messageStop',
	'iterationLimitReached',
	'error',
] as const satisfies readonly ChatStreamEvent['type'][];

const chatStreamEventTypeSet = new Set<string>(chatStreamEventTypes);

export const DEFAULT_TOOL_APPROVAL_LEVEL: ToolApprovalLevel = 'askDestructive';

export interface StreamChatOptions {
	tools?: ChatToolSelection;
	signal?: AbortSignal;
}

export async function* streamChat(
	messages: ChatMessage[],
	context: AiContextItem[],
	options: StreamChatOptions,
): AsyncGenerator<ChatStreamChunk, void, void> {
	const response = await requestChatStream(messages, context, options);

	for await (const payload of streamSseData(response.body)) {
		const chunk = parseChatStreamChunk(payload);

		yield chunk;

		if (chunk === '[DONE]') return;
	}
}

export function createChatRequestBody(
	messages: ChatMessage[],
	context: AiContextItem[],
	tools?: ChatToolSelection,
): ChatRequestBody {
	return {
		messages,
		context,
		approvalLevel: DEFAULT_TOOL_APPROVAL_LEVEL,
		...(tools ? { tools } : {}),
	};
}

async function requestChatStream(
	messages: ChatMessage[],
	context: AiContextItem[],
	options: StreamChatOptions,
): Promise<Response & { body: ReadableStream<Uint8Array> }> {
	const response = await fetch(
		joinURL(
			useProjectApiBaseUrl(),
			'ai/chat',
		),
		{
			method: 'POST',
			headers: {
				'Accept': 'text/event-stream',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(createChatRequestBody(messages, context, options.tools)),
			credentials: 'include',
			signal: options.signal,
		},
	);

	if (!response.ok) {
		throw new Error(await getResponseErrorMessage(response));
	}

	if (!response.body) {
		throw new Error('Chat stream response body is missing');
	}

	return response as Response & { body: ReadableStream<Uint8Array> };
}

async function* streamSseData(
	body: ReadableStream<Uint8Array>,
): AsyncGenerator<string, void, void> {
	const reader = body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';

	while (true) {
		const { value, done } = await reader.read();

		buffer += decoder.decode(value, { stream: !done });

		let separatorMatch = /(?:\r?\n){2}/.exec(buffer);

		while (separatorMatch) {
			const frame = buffer.slice(0, separatorMatch.index);
			buffer = buffer.slice(separatorMatch.index + separatorMatch[0].length);

			const payload = getSseDataPayload(frame);

			if (payload) {
				yield payload;
			}

			separatorMatch = /(?:\r?\n){2}/.exec(buffer);
		}

		if (!done) continue;

		const payload = getSseDataPayload(buffer);

		if (payload) {
			yield payload;
		}

		return;
	}
}

function getSseDataPayload(frame: string): string {
	return frame
		.split(/\r?\n/)
		.filter((line) => line.startsWith('data:'))
		.map((line) => line.slice(5).trimStart())
		.join('\n');
}

function parseChatStreamChunk(payload: string): ChatStreamChunk {
	if (payload === '[DONE]') return payload;

	const parsed = JSON.parse(payload) as unknown;

	if (!isChatStreamEvent(parsed)) {
		throw new Error('Invalid chat stream event');
	}

	return parsed;
}

function isChatStreamEvent(value: unknown): value is ChatStreamEvent {
	if (!value || typeof value !== 'object') return false;
	if (!('type' in value) || typeof value.type !== 'string') return false;
	return chatStreamEventTypeSet.has(value.type);
}

async function getResponseErrorMessage(response: Response): Promise<string> {
	const fallbackMessage = `Chat request failed with status ${response.status}`;

	try {
		const body = await response.text();

		if (!body) return fallbackMessage;

		const parsed = JSON.parse(body) as unknown;

		if (
			parsed
			&& typeof parsed === 'object'
			&& 'message' in parsed
			&& typeof parsed.message === 'string'
		) {
			return parsed.message;
		}

		return fallbackMessage;
	}
	catch {
		return fallbackMessage;
	}
}
